import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";

import * as fromStore from "../../../../store";
import * as fromSharedStore from "gematik-shared";

import { Store } from "@ngrx/store";
import { Observable, Subscription, combineLatest, filter, map, switchMap } from "rxjs";

@Component({
  selector: "side-navigation",
  styleUrls: ["side-navigation.component.scss"],
  template: `
    <mat-nav-list>
      <mat-list-item *ngIf="!isHandset" data-testid="toggler" (click)="onToggle()" class="text-end">
        <mat-icon mat-list-icon class="chevron-button" [ngClass]="{ rotated: isExpanded }"
          >chevron_right
        </mat-icon>
      </mat-list-item>
      <div class="d-flex align-items-startxx flex-column mt-3 ms-3 me-3">
        <div class="d-flex justify-content-between">
          <div routerLink="/" style="cursor: pointer">
            <div *ngIf="isExpanded">
              <img src="/gematik-shared/assets/icons/logo.svg" alt="" />
              <span class="gem-color-dark-grey ms-1">myServices</span>
            </div>
            <div *ngIf="!isExpanded">
              <img src="/assets/icons/portal/logo-small.svg" alt="" />
            </div>
          </div>
          <div *ngIf="isHandset">
            <mat-icon
              class="d-block nav-icons"
              style="cursor: pointer"
              svgIcon="gem-close"
              (click)="onCloseHandsetMenu()"
            ></mat-icon>
          </div>
        </div>
        <div class="w-100">
          <profile-display [isExpanded]="isExpanded"></profile-display>
        </div>
        <div class="d-flex justify-content-center mt-2 w-100">
          <hr class="w-100" />
        </div>
        <div>
          <side-navigation-item
            *ngFor="let nav of navs"
            [treeContent]="nav"
            [isExpanded]="isExpanded"
          ></side-navigation-item>
        </div>
        <div class="w-100">
          <footer
            [isExpanded]="isExpanded"
            (closeHandsetMenu)="onCloseHandsetMenu()"
            (startGuidedTour)="onStartGuidedTour($event)"
          ></footer>
        </div>
      </div>
    </mat-nav-list>
  `,
  standalone: false,
})
export class SideNavigationComponent implements OnInit, OnDestroy {
  @Input() isExpanded: boolean;
  @Input() isHandset: boolean;

  @Output() toggle: EventEmitter<any> = new EventEmitter();
  @Output() closeHandsetMenu: EventEmitter<any> = new EventEmitter();
  @Output() startGuidedTour: EventEmitter<number> = new EventEmitter();

  myOpenActivities$: Observable<number> = this.store
    .select(fromStore.getMyTaskTotals)
    .pipe(map((totals) => totals.open));

  groupOpenActivities$: Observable<number> = this.store
    .select(fromStore.getGroupTaskTotals)
    .pipe(map((totals) => totals.open));

  myOpenProcesses$: Observable<number> = this.store
    .select(fromStore.getMyProcessTotals)
    .pipe(map((totals) => totals.open));

  observableOpenProcesses$: Observable<number> = this.store
    .select(fromStore.getObservableProcessTotals)
    .pipe(map((totals) => totals.open));

  navs: {
    rootLabel: string;
    tooltip: string;
    icon: string;
    route?: string;
    links?: { label: string; tooltip: string; route: string; badge?: number | Observable<number> }[];
    treeElementOpened?: boolean;
  }[] = [
    {
      rootLabel: "uwl-oveview",
      tooltip: "menu-help.uwl-oveview",
      icon: "gem-dashboard",
      route: "/dashboard",
    },
    {
      rootLabel: "uwl-tasks",
      tooltip: "menu-help.uwl-tasks",
      icon: "gem-report",
      route: "/tasks",
      links: [
        { label: "uwl-tasks-my", tooltip: "menu-help.uwl-tasks-my", route: "tasks/my", badge: this.myOpenActivities$ },
        { label: "uwl-tasks-group", tooltip: "menu-help.uwl-tasks-group", route: "tasks/group", badge: this.groupOpenActivities$ },
      ],
      treeElementOpened: true,
    },
    {
      rootLabel: "uwl-processes",
      tooltip: "menu-help.uwl-processes",
      icon: "gem-mediation",
      route: "/processes",
      links: [
        { label: "uwl-processes-my", tooltip: "menu-help.uwl-processes-my", route: "processes/my", badge: this.myOpenProcesses$ },
        {
          label: "uwl-processes-subscriber", tooltip: "menu-help.uwl-processes-subscriber",
          route: "processes/subscriber",
          badge: this.observableOpenProcesses$,
        },
      ],
      treeElementOpened: true,
    },
  ];

  subscriptions: Subscription[] = [];

  constructor(private store: Store<fromStore.UwlState>) {}

  ngOnInit(): void {
    const sub = this.store
      .select(fromSharedStore.getGroupsLoaded)
      .pipe(
        filter((loaded) => loaded === true),
        switchMap(() =>
          combineLatest([
            this.store.select(fromSharedStore.isUserAdmin),
            this.store.select(fromSharedStore.isUserKpiReader),
            this.store.select(fromSharedStore.isUserAiAdmin),
          ]),
        ),
      )
      .subscribe(([isAdmin, isKpiReader, isAiAdmin]) => {
        const chartsIndex = this.navs.findIndex((nav) => nav.rootLabel === "uwl-charts");
        if ((isKpiReader || isAdmin) && chartsIndex === -1) {
          this.navs.push({
            rootLabel: "uwl-charts",
      tooltip: "menu-help.uwl-charts",
            icon: "gem-add-chart",
            route: "/charts",
          });
        }
        const adminIndex = this.navs.findIndex((nav) => nav.rootLabel === "uwl-administration");
        if ((isAdmin || isAiAdmin) && adminIndex === -1) {
          const adminLinks = [];
          if (isAdmin) {
            adminLinks.push(
              { label: "uwl-config", tooltip: "menu-help.uwl-config", route: "/endpoints-config" },
              { label: "direct-onboarding-config", tooltip: "menu-help.direct-onboarding-config", route: "/direct-onboarding-config" },
              { label: "incidents", tooltip: "menu-help.incidents", route: "/incidents" },
              { label: "templates", tooltip: "menu-help.templates", route: "/templates" },
              { label: "alerts", tooltip: "menu-help.alerts", route: "/alerts" },
              { label: "uwl-help", tooltip: "menu-help.uwl-help-config", route: "/help-config" },
              { label: "redis-cache.title", tooltip: "menu-help.redis-cache", route: "/redis-cache" },
              { label: "uwl-feedback", tooltip: "menu-help.uwl-feedback", route: "/feedbacks" },
              { label: "uwl-crm-sync", tooltip: "menu-help.uwl-crm-sync", route: "/sync-admin" },
              { label: "license-admin", tooltip: "menu-help.license-admin", route: "/license-admin" },
            );
          }
          if (isAiAdmin) {
            adminLinks.push({ label: "uwl-myassist", tooltip: "menu-help.uwl-myassist", route: "/myassist" });
          }
          this.navs.push({
            rootLabel: "uwl-administration",
      tooltip: "menu-help.uwl-administration",
            icon: "admin-panel-settings",
            links: adminLinks,
            treeElementOpened: false,
          });
        }
      });

    this.subscriptions.push(sub);
  }

  onToggle(): void {
    this.toggle.emit();
  }

  onCloseHandsetMenu(): void {
    this.closeHandsetMenu.emit();
  }

  onStartGuidedTour(id: number): void {
    this.startGuidedTour.emit(id);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
